import axios from 'axios';

const BASE_URL = '/notification';

type GetNotificationsParams = {
  priority?: string;
  includeRead?: boolean;
  includeDismissed?: boolean;
  limit?: number;
  daysBack?: number;
};

// Counts reflect the full server-side dataset, not just the returned subset (which may be filtered/paginated)
type GetNotificationsResponse = {
  notifications: NotificationResponse[];
  totalCount: number;
  unreadCount: number;
};

type NotificationResponse = {
  notificationID: number;
  notificationType: string;
  priority: string;
  category: string;
  title: string;
  description: string;
  metadataJson?: string | null;
  isRead: boolean;
  isDismissed: boolean;
  createdUtc: string;
  readUtc?: string | null;
  dismissedUtc?: string | null;
  actions?: Array<{ label: string; route?: string }>;
};

type GetUnreadCountResponse = {
  unreadCount: number;
  criticalCount: number;
  warningCount: number;
  infoCount: number;
};

type MarkAsReadResponse = {
  success: boolean;
};

type MarkAllAsReadResponse = {
  updatedCount: number;
};

type DismissNotificationResponse = {
  success: boolean;
};

type CreateNotificationPayload = {
  notificationType: string;
  priority: string;
  category: string;
  title: string;
  description: string;
  metadataJson?: string | null;
  cooldownHours?: number | null;
};

type CreateNotificationResponse = {
  notificationID: number;
  createdUtc: string;
  isDuplicate: boolean;
};

export const NotificationHubApiService = {
  getNotifications: async (params: GetNotificationsParams = {}): Promise<GetNotificationsResponse> => {
    const response = await axios.get(BASE_URL, { params });
    return response.data;
  },

  getUnreadCount: async (): Promise<GetUnreadCountResponse> => {
    const response = await axios.get(`${BASE_URL}/unread-count`);
    return response.data;
  },

  markAsRead: async (notificationID: number): Promise<MarkAsReadResponse> => {
    const response = await axios.post(`${BASE_URL}/${notificationID}/read`);
    return response.data;
  },

  markAllAsRead: async (priority?: string): Promise<MarkAllAsReadResponse> => {
    const params = priority ? { priority } : {};
    const response = await axios.post(`${BASE_URL}/read-all`, null, { params });
    return response.data;
  },

  dismissNotification: async (notificationID: number): Promise<DismissNotificationResponse> => {
    const response = await axios.delete(`${BASE_URL}/${notificationID}`);
    return response.data;
  },

  createNotification: async (payload: CreateNotificationPayload): Promise<CreateNotificationResponse> => {
    const response = await axios.post(BASE_URL, payload);
    return response.data;
  },
};
